/**
 * Allocation-specific hooks using TanStack Query
 * Provides data fetching, caching, and mutations for allocation operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useModuleGate } from '@/hooks/useAccess';
import { ALLOCATION_READ_MODULES } from '@/lib/modules';
import { AllocationsService } from '@/api/services/AllocationsService';
import type {
  AllocationResponse,
  AllocationListResponse,
  CreateAllocationRequest,
  UpdateAllocationRequest,
} from '@/types/capacity';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { calendarKeys } from './useCalendarWeek';
import { groupKeys } from './useGroups';
import { capacityKeys } from './useCapacity';

// Query keys for allocations
export const allocationKeys = {
  all: ['allocations'] as const,
  lists: () => [...allocationKeys.all, 'list'] as const,
  list: (filters: Record<string, string | number | undefined>) => [...allocationKeys.lists(), { filters }] as const,
  byChild: (childId: string) => [...allocationKeys.lists(), 'byChild', childId] as const,
  details: () => [...allocationKeys.all, 'detail'] as const,
  detail: (id: string) => [...allocationKeys.details(), id] as const,
};

// Hook to fetch all allocations
export function useAllocations(
  childId?: string,
  groupId?: string,
  contractId?: string,
  limit = 50,
  offset = 0,
  fromDate?: string,
  toDate?: string,
  options?: { enabled?: boolean }
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();
  const canReadAllocations = useModuleGate(ALLOCATION_READ_MODULES);

  return useQuery({
    queryKey: allocationKeys.list({ childId, groupId, contractId, limit, offset, fromDate, toDate }),
    queryFn: async () => {
      const response = await AllocationsService.getApiV1Allocations(
        limit,
        offset,
        childId,
        groupId,
        contractId,
        fromDate,
        toDate
      );
      return response as AllocationListResponse;
    },
    // `options` is a caller-supplied gate, not a filter — it stays out of the queryKey.
    enabled: isSignedIn && isAuthReady && canReadAllocations && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error instanceof Error && (
        error.message.includes('401') ||
        error.message.includes('403') ||
        error.message.includes('Unauthorized')
      )) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

// Hook to fetch a single allocation
export function useAllocation(id: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: allocationKeys.detail(id),
    queryFn: async () => {
      const response = await AllocationsService.getApiV1Allocations1(id);
      return response as AllocationResponse;
    },
    enabled: !!id && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to create a new allocation
// `silent` suppresses the success toast. The capacity page creates one allocation
// per age period for one action and reports once for all of them.
export function useCreateAllocation(options?: {
  /** No success toast: the caller reports once for the action. */
  silent?: boolean;
}) {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: async (allocation: CreateAllocationRequest) => {
      const response = await AllocationsService.postApiV1Allocations(allocation);
      return response as AllocationResponse;
    },
    onSuccess: async (data, variables) => {
      // Set the created allocation data in the detail cache
      if (data?.id) {
        queryClient.setQueryData(allocationKeys.detail(data.id), data);
      }

      // Targeted invalidation: only invalidate queries that actually need updating
      // Performance optimization: avoid invalidating all ['capacity'] queries
      const childId = variables.child_id;

      // Invalidate allocation lists for this child
      await queryClient.invalidateQueries({
        queryKey: allocationKeys.lists(),
        refetchType: 'active',
      });

      // Invalidate only the specific child's sync status and categorization queries
      if (childId) {
        await queryClient.invalidateQueries({
          queryKey: capacityKeys.childSync(childId),
          refetchType: 'active',
        });
      }
      // Invalidate categorization list (needed for status badges)
      await queryClient.invalidateQueries({
        queryKey: capacityKeys.children(),
        refetchType: 'active',
      });

      // Invalidate calendar queries to update calendar views
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.all,
        refetchType: 'active',
      });

      // Invalidate group queries to update group views
      await queryClient.invalidateQueries({
        queryKey: groupKeys.all,
        refetchType: 'active',
      });

      if (!silent) {
        toast.success(i18n._('capacity.allocation.toast.createSuccess'));
      }
    },
    onError: (error: Error) => {
      const errorMessage = error.message || "Unknown error";
      const lowerMessage = errorMessage.toLowerCase();
      
      // Check for duplicate allocation
      if (lowerMessage.includes("duplicate") || lowerMessage.includes("already exists")) {
        toast.error(i18n._('common.error.duplicateEntry'));
        return;
      }
      
      // Handle validation errors
      if (lowerMessage.includes("validation") || lowerMessage.includes("invalid")) {
        toast.error(i18n._('common.error.validationFailed'));
        return;
      }
      
      // Handle overlapping allocations
      if (lowerMessage.includes("overlap")) {
        toast.error(i18n._('capacity.allocation.error.overlap'));
        return;
      }
      
      // Generic error fallback
      toast.error(i18n._('capacity.allocation.toast.createFailed'));
    },
  });
}

// Hook to update an allocation
export function useUpdateAllocation() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAllocationRequest }) => {
      const response = await AllocationsService.putApiV1Allocations(id, data);
      return response as AllocationResponse;
    },
    onSuccess: async (data, variables) => {
      // Update the detail cache immediately with the fresh data from the server
      queryClient.setQueryData(allocationKeys.detail(variables.id), data);

      // Update any list queries that contain this allocation optimistically
      queryClient.setQueriesData<AllocationListResponse>(
        { queryKey: allocationKeys.lists() },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            allocations: (oldData.allocations ?? []).map((allocation) =>
              allocation.id === variables.id ? data : allocation
            ),
          };
        }
      );

      // Targeted invalidation: only invalidate queries that actually need updating
      // Performance optimization: avoid invalidating all ['capacity'] queries
      const childId = data?.child_id;

      // Invalidate allocation lists and WAIT for refetch
      await queryClient.invalidateQueries({
        queryKey: allocationKeys.lists(),
        refetchType: 'active',
      });

      // Invalidate only the specific child's sync status
      if (childId) {
        await queryClient.invalidateQueries({
          queryKey: capacityKeys.childSync(childId),
          refetchType: 'active',
        });
      }
      // Invalidate categorization list (needed for status badges)
      await queryClient.invalidateQueries({
        queryKey: capacityKeys.children(),
        refetchType: 'active',
      });

      // Invalidate calendar queries to update calendar views
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.all,
        refetchType: 'active',
      });

      // Invalidate group queries to update group views
      await queryClient.invalidateQueries({
        queryKey: groupKeys.all,
        refetchType: 'active',
      });

      toast.success(i18n._('capacity.allocation.toast.updateSuccess'));
    },
    onError: (error: Error) => {
      const errorMessage = error.message || "Unknown error";
      const lowerMessage = errorMessage.toLowerCase();

      // Handle optimistic locking conflicts (409)
      if (lowerMessage.includes("409") || lowerMessage.includes("concurrent modification")) {
        toast.error(i18n._('capacity.allocation.error.concurrentModification'));
        return;
      }

      // Handle validation errors
      if (lowerMessage.includes("validation") || lowerMessage.includes("invalid")) {
        toast.error(i18n._('common.error.validationFailed'));
        return;
      }

      // Generic error fallback
      toast.error(i18n._('capacity.allocation.toast.updateFailed'));
    },
  });
}

// Hook to delete an allocation
// Pass childId for targeted cache invalidation (performance optimization)
export function useDeleteAllocation() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ id, childId }: { id: string; childId?: string }) => {
      await AllocationsService.deleteApiV1Allocations(id);
      return { childId }; // Return childId for onSuccess
    },
    onSuccess: async (result, variables) => {
      // Targeted invalidation: only invalidate queries that actually need updating
      // Performance optimization: avoid invalidating all ['capacity'] queries
      const childId = result?.childId || variables.childId;

      // Invalidate allocation lists
      await queryClient.invalidateQueries({
        queryKey: allocationKeys.lists(),
        refetchType: 'active',
      });

      // Remove the deleted allocation from detail cache
      queryClient.removeQueries({ queryKey: allocationKeys.detail(variables.id) });

      // Invalidate only the specific child's sync status
      if (childId) {
        await queryClient.invalidateQueries({
          queryKey: capacityKeys.childSync(childId),
          refetchType: 'active',
        });
      }
      // Invalidate categorization list (needed for status badges)
      await queryClient.invalidateQueries({
        queryKey: capacityKeys.children(),
        refetchType: 'active',
      });

      // Invalidate calendar queries to update calendar views
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.all,
        refetchType: 'active',
      });

      // Invalidate group queries to update group views
      await queryClient.invalidateQueries({
        queryKey: groupKeys.all,
        refetchType: 'active',
      });

      toast.success(i18n._('capacity.allocation.toast.deleteSuccess'));
    },
    onError: () => {
      toast.error(i18n._('capacity.allocation.toast.deleteFailed'));
    },
  });
}
